import { apiClient } from './request';
import { create, fromJson, type JsonValue, toJson } from '@bufbuild/protobuf';
import { type Agent, AgentSchema } from '@/proto/agentplatform/v1/agent_pb';
import {
  CreateAgentRequestSchema,
  CreateAgentResponseSchema,
  GetAgentRequestSchema,
  GetAgentResponseSchema,
  UpdateAgentRequestSchema,
  UpdateAgentResponseSchema,
  DeleteAgentRequestSchema,
  DeleteAgentResponseSchema,
  ListAgentsResponseSchema,
  RestartAgentRequestSchema,
  RestartAgentResponseSchema,
  ListInternalToolkitsResponseSchema,
  type InternalToolkit,
} from '@/proto/agentplatform/httpv2/agent_server_pb';
import { type ListRequest, ListRequestSchema } from '@/proto/agentplatform/httpv2/common_pb';

/** 列表返回的通用形状，兼容页面既有的 `data.data` / `data.total` 读法。 */
export interface AgentListResult {
  data: AgentWithPreHooks[];
  total: number;
}

export interface InternalToolkitListResult {
  data: InternalToolkit[];
}

/** 前置钩子判定结果映射的动作（protojson 枚举名）；FALLTHROUGH 表示交给下一个钩子 */
export type ToolPermissionPreHookAction =
  | 'TOOL_PERMISSION_PRE_HOOK_ACTION_DENY'
  | 'TOOL_PERMISSION_PRE_HOOK_ACTION_REQUIRE_HUMAN'
  | 'TOOL_PERMISSION_PRE_HOOK_ACTION_AUTO_APPROVE'
  | 'TOOL_PERMISSION_PRE_HOOK_ACTION_FALLTHROUGH';

/** 前置钩子判定方式（protojson 枚举名）；HTTP 是枚举零值，后端响应中省略 type 即为 HTTP */
export type ToolPermissionPreHookType =
  'TOOL_PERMISSION_PRE_HOOK_TYPE_HTTP' | 'TOOL_PERMISSION_PRE_HOOK_TYPE_AI';

/**
 * 工具权限前置判定钩子，即 advanced_config.tool_permissions.pre_hooks 的元素。
 * 后端用 protojson 输出，会省略零值（HTTP 类型、未设置的动作、空串和 0），因此这些字段可选。
 */
export interface ToolPermissionPreHook {
  /** 链内唯一；HTTP 类型会回传给判定服务 */
  name: string;
  type?: ToolPermissionPreHookType;
  on_true: ToolPermissionPreHookAction;
  /** AI 类型不能为 AUTO_APPROVE */
  on_false?: ToolPermissionPreHookAction;
  /** 至少一个工具名，不支持 "*" */
  tool_match: string[];
  /** http 或 https 地址；HTTP 类型必填，AI 类型不能配置 */
  endpoint_url?: string;
  /** AI 类型的提示词（判定标准），留空使用 framework 内置提示词；HTTP 类型不能配置 */
  prompt?: string;
  /** 单次判定超时（秒）；0 使用 framework 默认（HTTP 3.0，AI 30.0） */
  timeout?: number;
  /** 重试次数；0 使用 framework 默认 1 */
  retries?: number;
  /** 不能为 AUTO_APPROVE */
  on_error?: ToolPermissionPreHookAction;
}

/** src/proto 已冻结且没有 tool_permissions.pre_hooks，前置钩子随 Agent 单独携带 */
export type AgentWithPreHooks = Agent & { toolPreHooks: ToolPermissionPreHook[]; interleavedThinking?: boolean };

/** pre_hooks 在 Agent JSON 及 Create/UpdateAgentRequest JSON 中的位置 */
interface PreHooksJson {
  advanced_config?: { tool_permissions?: { pre_hooks?: ToolPermissionPreHook[] } };
}

/** 从响应的 Agent JSON 中取出并原地删除 pre_hooks，否则 fromJson 会因未知字段抛错 */
function takeToolPreHooks(agent: PreHooksJson | undefined): ToolPermissionPreHook[] {
  const permissions = agent?.advanced_config?.tool_permissions;
  const hooks = permissions?.pre_hooks ?? [];
  delete permissions?.pre_hooks;
  return hooks;
}

function putToolPreHooks(body: PreHooksJson, hooks: ToolPermissionPreHook[]) {
  if (hooks.length === 0) return;
  const advancedConfig = (body.advanced_config ??= {});
  (advancedConfig.tool_permissions ??= {}).pre_hooks = hooks;
}

interface ModelConfigJson {
  model_config?: { extra_config?: { support_interleaved_thinking?: boolean } };
}

function takeInterleavedThinking(agent: ModelConfigJson | undefined): boolean | undefined {
  const extra = agent?.model_config?.extra_config;
  const value = extra?.support_interleaved_thinking;
  delete extra?.support_interleaved_thinking;
  return value;
}

function putInterleavedThinking(body: ModelConfigJson, value: boolean | undefined) {
  if (value === undefined) return;
  const model = (body.model_config ??= {});
  (model.extra_config ??= {}).support_interleaved_thinking = value;
}

function parseAgentResponse(
  schema:
    | typeof GetAgentResponseSchema
    | typeof CreateAgentResponseSchema
    | typeof UpdateAgentResponseSchema,
  body: JsonValue
): AgentWithPreHooks {
  const toolPreHooks = takeToolPreHooks((body as { data?: PreHooksJson }).data);
  const interleavedThinking = takeInterleavedThinking((body as { data?: ModelConfigJson }).data);
  const agent = fromJson(schema, body).data ?? create(AgentSchema, {});
  return { ...agent, toolPreHooks, interleavedThinking };
}

export const agentApi = {
  listAgents: async (params: ListRequest): Promise<AgentListResult> => {
    const responseObject = await apiClient.post<JsonValue>(
      '/v2/ListAgents',
      toJson(ListRequestSchema, params)
    );
    const items = (responseObject.data as {
      data?: { items?: (PreHooksJson & ModelConfigJson)[] };
    }).data?.items;
    const extras = items?.map((item) => ({
      toolPreHooks: takeToolPreHooks(item),
      interleavedThinking: takeInterleavedThinking(item),
    })) ?? [];
    const resp = fromJson(ListAgentsResponseSchema, responseObject.data);
    return {
      data: (resp.data?.items ?? []).map((agent, i) => ({ ...agent, ...extras[i] })),
      total: resp.data?.total ?? 0,
    };
  },

  getAgent: async (id: string): Promise<AgentWithPreHooks> => {
    const req = create(GetAgentRequestSchema, { id });
    const responseObject = await apiClient.post<JsonValue>(
      '/v2/GetAgent',
      toJson(GetAgentRequestSchema, req)
    );
    return parseAgentResponse(GetAgentResponseSchema, responseObject.data);
  },

  createAgent: async (data: AgentWithPreHooks): Promise<AgentWithPreHooks> => {
    const req = create(CreateAgentRequestSchema, {
      name: data.name,
      description: data.description,
      modelConfig: data.modelConfig,
      channelConfigs: data.channelConfigs,
      pluginConfigs: data.pluginConfigs,
      advancedConfig: data.advancedConfig,
    });
    const body = toJson(CreateAgentRequestSchema, req);
    putToolPreHooks(body as PreHooksJson, data.toolPreHooks);
    putInterleavedThinking(body as ModelConfigJson, data.interleavedThinking);
    const responseObject = await apiClient.post<JsonValue>('/v2/CreateAgent', body);
    return parseAgentResponse(CreateAgentResponseSchema, responseObject.data);
  },

  updateAgent: async (id: string, data: AgentWithPreHooks): Promise<AgentWithPreHooks> => {
    const req = create(UpdateAgentRequestSchema, {
      id,
      name: data.name,
      description: data.description,
      modelConfig: data.modelConfig,
      channelConfigs: data.channelConfigs,
      pluginConfigs: data.pluginConfigs,
      advancedConfig: data.advancedConfig,
    });
    const body = toJson(UpdateAgentRequestSchema, req);
    putToolPreHooks(body as PreHooksJson, data.toolPreHooks);
    putInterleavedThinking(body as ModelConfigJson, data.interleavedThinking);
    const responseObject = await apiClient.post<JsonValue>('/v2/UpdateAgent', body);
    return parseAgentResponse(UpdateAgentResponseSchema, responseObject.data);
  },

  deleteAgent: async (id: string): Promise<void> => {
    const req = create(DeleteAgentRequestSchema, { id });
    const responseObject = await apiClient.post<JsonValue>(
      '/v2/DeleteAgent',
      toJson(DeleteAgentRequestSchema, req)
    );
    fromJson(DeleteAgentResponseSchema, responseObject.data);
  },

  restartAgent: async (id: string): Promise<void> => {
    const req = create(RestartAgentRequestSchema, { id });
    const responseObject = await apiClient.post<JsonValue>(
      '/v2/RestartAgent',
      toJson(RestartAgentRequestSchema, req)
    );
    // ignoreUnknownFields: 兼容后端 envelope 增字段，避免 HTTP 200 却因 fromJson 抛错被当成失败
    fromJson(RestartAgentResponseSchema, responseObject.data, {
      ignoreUnknownFields: true,
    });
  },

  listInternalToolkits: async (): Promise<InternalToolkitListResult> => {
    // 该 RPC 入参为 google.protobuf.Empty，无需分页/过滤；POST 空体即可。
    const responseObject = await apiClient.post<JsonValue>('/v2/ListInternalToolkits', {});
    const resp = fromJson(ListInternalToolkitsResponseSchema, responseObject.data);
    return { data: resp.data?.items ?? [] };
  },
};
